import React, { useState, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material'

import { IconLanguage } from '@tabler/icons'
import { MainCard, Transitions } from './../../../components/Main'
import { setItem } from '../../../utility/LocalStorage'

const ProfileSection = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const customization = useSelector((state) => state.customization)
  const [selectedIndex] = useState(-1)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  const setLanguage = (lang) => {
    setItem('language', lang)
    window.location.reload(false)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <>
      <Avatar
        variant="rounded"
        sx={{
          ...theme.typography.commonAvatar,
          ...theme.typography.mediumAvatar,
          transition: 'all .2s ease-in-out',
          background: 'transparent',
          color: theme.palette.grey[700],
          '&[aria-controls="menu-list-grow"],&:hover': {
            background: 'transparent',
            color: theme.palette.primary.main,
          },
        }}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        color="inherit"
        onClick={handleToggle}
      >
        <IconLanguage stroke={1.5} size="3rem" />
      </Avatar>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  <Box sx={{ p: 2, pb: 0 }}>
                    <Stack sx={{ pb: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">{t('title.select_language')}</Typography>
                      </Stack>
                    </Stack>
                    <Divider />
                  </Box>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <List
                      component="nav"
                      sx={{
                        width: '100%',
                        maxWidth: 350,
                        minWidth: 300,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: '10px',
                        color: theme.palette.grey[50],
                        [theme.breakpoints.down('md')]: {
                          minWidth: '100%',
                        },
                        '& .MuiListItemButton-root': {
                          mt: 0.5,
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 0}
                        onClick={() => {
                          setLanguage('en')
                        }}
                      >
                        <ListItemIcon>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="h4">{t('languages.en')}</Typography>
                          </Stack>
                        </ListItemIcon>
                        <ListItemText primary={<Typography>{t('languages.english')}</Typography>} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 0}
                        onClick={() => {
                          setLanguage('id')
                        }}
                      >
                        <ListItemIcon>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="h4">{t('languages.id')}</Typography>
                          </Stack>
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography>{t('languages.indonesian')}</Typography>}
                        />
                      </ListItemButton>
                    </List>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  )
}

export default ProfileSection
